import React, { BaseSyntheticEvent, FC, useMemo } from 'react';

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  Select,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { useErrorMessage } from '../index';
import { useStyles } from '../index';
import { FieldProps, SelectFieldSchema, SelectFieldStyles } from '../types';

import { FormLabel } from './FormLabel';

export const SelectField: FC<FieldProps<SelectFieldSchema>> = ({
  id,
  name,
  field,
  style,
}) => {
  const {
    label,
    helperText,
    isRequired,
    helpIcon,
    shouldDisplay,
    onChange,
    isDisabled,
    placeholder,
    helpTooltip,
    styles = {},
    onClickHelpIcon,
  } = field;

  const { register, watch } = useFormContext();

  // react-hook-form v7: watch() takes no options (`nest` was v6).
  const values = watch();

  const fieldStyles = useStyles<SelectFieldStyles>('selectField', styles);

  const errorMessage = useErrorMessage(name, label);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  const registered = register(name);

  // Chain react-hook-form's onChange so the pick reaches the form values.
  const handleOnChange = (e: BaseSyntheticEvent) => {
    registered.onChange(e);
    onChange?.(e.target.value, e);
  };

  return isVisible ? (
    <FormControl
      key={`${name}-control`}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
      {...fieldStyles.control}
    >
      {!!label && (
        <FormLabel
          htmlFor={name}
          label={label}
          helpIcon={helpIcon}
          styles={fieldStyles.label}
          helpTooltip={helpTooltip}
          isRequired={isRequired}
          onClickHelpIcon={onClickHelpIcon}
        />
      )}
      <Select
        // v7 register() returns props; passing it as a ref (v6 API) crashed.
        {...registered}
        data-testid={id}
        placeholder={placeholder}
        isDisabled={isDisabled}
        {...fieldStyles.select}
        {...style?.styles}
        onChange={handleOnChange}
      >
        {field.options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label || option.value}
          </option>
        ))}
      </Select>
      {!!helperText && (
        <FormHelperText {...fieldStyles.helperText}>
          {helperText}
        </FormHelperText>
      )}
      <FormErrorMessage {...fieldStyles.errorMessage}>
        {errorMessage}
      </FormErrorMessage>
    </FormControl>
  ) : null;
};
